import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";
import {
  Plug,
  CheckCircle,
  ShoppingCart,
  DollarSign,
} from "lucide-react";
import {  INTEGRATION_API_} from "../../api/auth";
import Loading from "../ui/Loading";
import ServiceUnavailable from "../component/ServiceUnavailable";


const COLORS = ["#4f46e5", "#ef4444"];

const Integration_dashboard = () => {
  const [stores, setStores] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ================= FETCH DATA ================= */
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [storesRes,
        ordersRes] = await Promise.all([
        INTEGRATION_API_.get("api/integrations/stores/"),
        INTEGRATION_API_.get("orders/orders/"),
      ]);

      setStores(storesRes.data);
      setOrders(ordersRes.data);
    } catch (err) {
      console.error("Dashboard error:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ================= KPI CALCULATIONS ================= */

  const totalIntegrations = stores.length;
  const activeIntegrations = stores.filter((s) => s.is_active).length;

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const monthlyOrdersRaw = orders.filter((order) => {
    const date = new Date(order.created_at);
    return (
      date.getMonth() === currentMonth &&
      date.getFullYear() === currentYear
    );
  });

  const monthlyRevenue = monthlyOrdersRaw.reduce(
    (sum, order) => sum + parseFloat(order.total_amount || 0),
    0
  );

  /* ================= GROUP ORDERS BY MONTH ================= */

  const monthlyMap = {};

  orders.forEach((order) => {
    const date = new Date(order.created_at);
    const month = date.toLocaleString("default", { month: "short" });

    if (!monthlyMap[month]) monthlyMap[month] = 0;
    monthlyMap[month]++;
  });

  const monthlyOrders = Object.keys(monthlyMap).map((key) => ({
    name: key,
    orders: monthlyMap[key],
  }));

  /* ================= GROUP BY PLATFORM ================= */

  const platformStats = {};

  orders.forEach((order) => {
    const platform = order.platform || "manual";

    if (!platformStats[platform]) {
      platformStats[platform] = {
        name: platform,
        total: 0,
        confirmed: 0,
        delivered: 0,
      };
    }

    platformStats[platform].total++;

    if (order.local_status === "confirmed")
      platformStats[platform].confirmed++;

    if (order.shipping_status === "delivered")
      platformStats[platform].delivered++;
  });

  const integrationStats = Object.values(platformStats);

  const integrationStatus = [
    { name: "Connected", value: activeIntegrations },
    { name: "Disconnected", value: totalIntegrations - activeIntegrations },
  ];

if (loading) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <Loading fullScreen text="Loading dashboard..." variant="dots" />
    </div>
  );
}

if (error) {
  return (
    <div className="min-h-screen bg-gray-50 p-6 dark:bg-slate-950">
      <div className="max-w-3xl mx-auto">
        <ServiceUnavailable label="Le service d'intégrations" onRetry={fetchData} />
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gray-50 p-6 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto space-y-10">

        <div>
          <h1 className="text-3xl font-semibold text-gray-800 dark:text-slate-100">
            Integration Dashboard
          </h1>
          <p className="text-gray-500 mt-1 dark:text-slate-500">
            Real-time integrations performance
          </p>
        </div>

        {/* KPI CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">

          <Card icon={<Plug />} value={totalIntegrations} label="Total Integrations" />
          <Card icon={<CheckCircle />} value={activeIntegrations} label="Active Integrations" />
          <Card icon={<ShoppingCart />} value={monthlyOrdersRaw.length} label="Orders This Month" />
          <Card icon={<DollarSign />} value={`${monthlyRevenue.toFixed(2)}`} label="Revenue This Month" />

        </div>

        {/* CHARTS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          <ChartCard title="Monthly Orders">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyOrders}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="orders" fill="#4f46e5" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Integration Status">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={integrationStatus}
                  outerRadius={100}
                  dataKey="value"
                  label
                >
                  {integrationStatus.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

        </div>

        {/* PLATFORM STATS */}
        <div className="bg-white rounded-2xl shadow-sm border p-6 dark:bg-slate-900 dark:border-slate-800">
          <h3 className="text-xl font-semibold mb-6 dark:text-slate-100">
            Orders by Integration
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrationStats.map((item, index) => {
              const confirmRate = Math.round((item.confirmed / item.total) * 100 || 0);
              const deliveredRate = Math.round((item.delivered / item.total) * 100 || 0);

              return (
                <div key={index} className="bg-gray-50 p-5 rounded-xl border dark:bg-slate-800 dark:border-slate-700">
                  <h4 className="font-semibold mb-3 capitalize dark:text-slate-100">{item.name}</h4>
                  <p className="dark:text-slate-300">Total: {item.total}</p>
                  <p className="text-green-600 dark:text-emerald-400">Confirmed: {item.confirmed}</p>
                  <p className="text-indigo-600 dark:text-indigo-400">Delivered: {item.delivered}</p>

                  <div className="mt-3 text-xs dark:text-slate-500">
                    Confirm: {confirmRate}% | Delivered: {deliveredRate}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

/* REUSABLE CARDS */
const Card = ({ icon, value, label }) => (
  <div className="bg-white rounded-2xl shadow-sm p-6 border dark:bg-slate-900 dark:border-slate-800">
    <div className="flex justify-between items-center">
      {icon}
      <span className="text-sm text-gray-400 dark:text-slate-500">Stats</span>
    </div>
    <h2 className="text-2xl font-semibold mt-4 dark:text-slate-100">{value}</h2>
    <p className="text-gray-500 text-sm dark:text-slate-500">{label}</p>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm p-6 border dark:bg-slate-900 dark:border-slate-800">
    <h3 className="text-lg font-semibold mb-4 dark:text-slate-100">{title}</h3>
    {children}
  </div>
);

export default Integration_dashboard;